import { useEffect, useState, useCallback, useMemo } from 'react'
import { Link } from 'react-router-dom'
import { supabase } from '../lib/supabase'
import { colors, formatNumber } from '../styles/tokens'
import { Button, Input, Select } from './FormControls'
import { EmptyState, ErrorBanner, SuccessBanner, SkeletonRows } from './Feedback'

// =======================================================================
// WbsManagement — Wave 5. A professional hierarchy management workspace,
// visually modeled on the real CostSpace Pro CostCodes.jsx tree pattern
// (expand/collapse via a Set, ▼/▶/· indicators, #1A7A4A green accents,
// #F3F4F6 selected/hover background) translated to this app's inline-
// style convention.
//
// STRICT RULE: all structural mutations (move, delete, reorder, BOQ
// assignment) go through the approved RPCs (move_wbs_node,
// delete_wbs_node_safe, reorder_wbs_siblings, assign_boq_to_wbs_node).
// This component performs no cost calculation — cost figures are read
// verbatim from wbs_package_total_cost.
// =======================================================================

export default function WbsManagement({ revisionId, tenderId }) {
  const [nodes, setNodes] = useState(null)
  const [boqCounts, setBoqCounts] = useState({})
  const [costByNode, setCostByNode] = useState({})
  const [revisionStatus, setRevisionStatus] = useState(null)
  const [expanded, setExpanded] = useState(new Set())
  const [selectedId, setSelectedId] = useState(null)
  const [error, setError] = useState(null)
  const [successMsg, setSuccessMsg] = useState(null)
  const [search, setSearch] = useState('')
  const [showAddForm, setShowAddForm] = useState(null) // parent_id or 'root'

  const isFrozen = revisionStatus === 'frozen'

  const load = useCallback(async () => {
    const revRes = await supabase.from('estimate_revision').select('status').eq('id', revisionId).single()
    if (revRes.error) return setError(revRes.error.message)
    const status = revRes.data?.status ?? null
    setRevisionStatus(status)

    if (status === 'frozen') {
      // P3 (WBS): same split as BoqTab/TenderSummaryPage -- Frozen never
      // reads the live wbs_snapshot_node / boq_item_wbs_map /
      // wbs_package_total_cost tables/View. Tree structure comes from
      // full_wbs_snapshot_data. Cost is intentionally partial: only
      // package_specific_costs (keyed by wbs_node_id, proven exact in
      // prior validation) is available in freeze_snapshot -- the "member
      // BOQ items' direct cost" component of the live wbs_package_total_cost
      // formula was never captured at freeze time (boq_item_wbs_map is
      // not part of the snapshot, and boq_item.package_name is null in
      // the actual data). Rather than inventing that missing half, nodes
      // with no captured package-specific cost are left undefined here,
      // which NodeDetailPanel already renders as "No cost data available"
      // -- a true statement, not a fabricated number.
      const snapRes = await supabase
        .from('freeze_snapshot')
        .select('full_wbs_snapshot_data, full_indirect_data')
        .eq('estimate_revision_id', revisionId)
        .maybeSingle()
      if (snapRes.error) return setError(snapRes.error.message)
      if (!snapRes.data) return setError('This revision is marked frozen but no snapshot record could be found.')

      const wbsNodes = Array.isArray(snapRes.data.full_wbs_snapshot_data) ? snapRes.data.full_wbs_snapshot_data : []
      const indirect = snapRes.data.full_indirect_data && typeof snapRes.data.full_indirect_data === 'object' ? snapRes.data.full_indirect_data : {}
      const pkgCosts = Array.isArray(indirect.package_specific_costs) ? indirect.package_specific_costs : []
      const boqWbsMap = Array.isArray(indirect.boq_item_wbs_map) ? indirect.boq_item_wbs_map : []

      setNodes(wbsNodes)

      const counts = {}
      for (const m of boqWbsMap) counts[m.wbs_snapshot_node_id] = (counts[m.wbs_snapshot_node_id] || 0) + 1
      setBoqCounts(counts) // empty for pre-contract frozen revisions (map never captured) -- same as before

      // wbs_package_total_cost's own formula, replayed over immutable
      // historical inputs: member_items_direct_cost_total (now
      // reconstructable via boq_item_wbs_map -> full_boq_data.direct_cost,
      // for revisions frozen after this data began being captured) +
      // package_specific_cost. No new financial rule -- same SUM the live
      // View performs, just over JSON instead of live tables. For
      // pre-contract frozen revisions (boq_item_wbs_map = []), this
      // naturally reduces to package_specific_cost alone, identical to
      // the prior partial behavior -- nothing regresses.
      const costs = {}
      for (const p of pkgCosts) {
        if (p.wbs_node_id != null) costs[p.wbs_node_id] = { packageSpecific: Number(p.package_specific_cost_with_markup) || 0, memberDirect: 0 }
      }
      if (boqWbsMap.length > 0) {
        const boqRes = await supabase.from('freeze_snapshot').select('full_boq_data').eq('estimate_revision_id', revisionId).maybeSingle()
        const fullBoq = Array.isArray(boqRes.data?.full_boq_data) ? boqRes.data.full_boq_data : []
        const dcByItem = Object.fromEntries(fullBoq.map((it) => [it.id, Number(it.direct_cost) || 0]))
        boqWbsMap.forEach((m) => {
          if (!costs[m.wbs_snapshot_node_id]) costs[m.wbs_snapshot_node_id] = { packageSpecific: 0, memberDirect: 0 }
          costs[m.wbs_snapshot_node_id].memberDirect += dcByItem[m.boq_item_id] || 0
        })
      }
      const costByNode = {}
      for (const [nodeId, c] of Object.entries(costs)) {
        costByNode[nodeId] = c.packageSpecific + c.memberDirect
      }
      setCostByNode(costByNode)
      setExpanded((prev) => (prev.size === 0 ? new Set(wbsNodes.map((n) => n.id)) : prev))
      return
    }

    const [nodesRes, mapRes, costRes] = await Promise.all([
      supabase.from('wbs_snapshot_node').select('*').eq('estimate_revision_id', revisionId).order('sort_order'),
      supabase.from('boq_item_wbs_map').select('wbs_snapshot_node_id').eq('estimate_revision_id', revisionId),
      supabase.from('wbs_package_total_cost').select('wbs_node_id, wbs_package_total_cost').eq('estimate_revision_id', revisionId),
    ])
    if (nodesRes.error) return setError(nodesRes.error.message)

    setNodes(nodesRes.data)

    const counts = {}
    for (const m of mapRes.data || []) counts[m.wbs_snapshot_node_id] = (counts[m.wbs_snapshot_node_id] || 0) + 1
    setBoqCounts(counts)

    const costs = {}
    for (const c of costRes.data || []) costs[c.wbs_node_id] = c.wbs_package_total_cost
    setCostByNode(costs)

    // Expand everything on first load only.
    setExpanded((prev) => (prev.size === 0 ? new Set((nodesRes.data || []).map((n) => n.id)) : prev))
  }, [revisionId])

  useEffect(() => { load() }, [load])

  const tree = useMemo(() => buildTree(nodes || []), [nodes])
  const selectedNode = (nodes || []).find((n) => n.id === selectedId) || null

  function toggleExpand(id) {
    setExpanded((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }
  function expandAll() { setExpanded(new Set((nodes || []).map((n) => n.id))) }
  function collapseAll() { setExpanded(new Set()) }

  async function handleAddNode(parentId, name, isPackage) {
    setError(null)
    const { error } = await supabase.from('wbs_snapshot_node').insert({
      estimate_revision_id: revisionId, parent_id: parentId === 'root' ? null : parentId, name, is_costing_package: isPackage,
    })
    if (error) setError(error.message)
    else { setSuccessMsg(`"${name}" added.`); setShowAddForm(null); await load() }
  }

  async function handleEditNode(nodeId, name, isPackage) {
    setError(null)
    const { error } = await supabase.from('wbs_snapshot_node').update({ name, is_costing_package: isPackage }).eq('id', nodeId)
    if (error) setError(error.message)
    else { setSuccessMsg('Node updated.'); await load() }
  }

  async function handleDelete(nodeId) {
    setError(null)
    const { error } = await supabase.rpc('delete_wbs_node_safe', { p_node_id: nodeId })
    if (error) setError(error.message)
    else {
      setSuccessMsg('Node deleted.')
      if (selectedId === nodeId) setSelectedId(null)
      await load()
    }
  }

  async function handleMove(nodeId, newParentId) {
    setError(null)
    const { error } = await supabase.rpc('move_wbs_node', { p_node_id: nodeId, p_new_parent_id: newParentId })
    if (error) setError(error.message)
    else { setSuccessMsg('Node moved.'); await load() }
  }

  async function handleReorder(nodeId, direction) {
    setError(null)
    const siblings = (nodes || []).filter((n) => n.parent_id === (nodes.find((x) => x.id === nodeId)?.parent_id ?? null)).sort((a, b) => a.sort_order - b.sort_order)
    const idx = siblings.findIndex((s) => s.id === nodeId)
    const swapIdx = direction === 'up' ? idx - 1 : idx + 1
    if (swapIdx < 0 || swapIdx >= siblings.length) return
    const reordered = [...siblings]
    ;[reordered[idx], reordered[swapIdx]] = [reordered[swapIdx], reordered[idx]]
    const { error } = await supabase.rpc('reorder_wbs_siblings', { p_ordered_node_ids: reordered.map((n) => n.id) })
    if (error) setError(error.message)
    else await load()
  }

  if (error && nodes === null) return <ErrorBanner message={error} />
  if (nodes === null) return <SkeletonRows rows={4} cols={2} />

  const filteredTree = search.trim() ? filterTree(tree, search.trim().toLowerCase()) : tree

  return (
    <div>
      <ErrorBanner message={error} />
      <SuccessBanner message={successMsg} />

      {isFrozen && (
        <div style={{ padding: '10px 14px', background: colors.warningBg, color: colors.warningText, borderRadius: 6, fontSize: 13, marginBottom: 16 }}>
          🔒 This revision is frozen — WBS structure and BOQ assignments are read-only.
        </div>
      )}

      <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 16, flexWrap: 'wrap' }}>
        <Input placeholder="Search WBS..." value={search} onChange={(e) => setSearch(e.target.value)} style={{ width: 220, fontSize: 13 }} />
        <Button variant="secondary" size="sm" onClick={expandAll}>▼ Expand All</Button>
        <Button variant="secondary" size="sm" onClick={collapseAll}>▶ Collapse All</Button>
        {!isFrozen && (
          <Button size="sm" onClick={() => setShowAddForm('root')}>+ Add Root Node</Button>
        )}
      </div>

      {showAddForm && (
        <NodeForm
          title={showAddForm === 'root' ? 'Add Root Node' : 'Add Child Node'}
          onSubmit={(name, isPackage) => handleAddNode(showAddForm, name, isPackage)}
          onCancel={() => setShowAddForm(null)}
        />
      )}

      <div style={{ display: 'grid', gridTemplateColumns: '1.4fr 1fr', gap: 20, alignItems: 'flex-start' }}>
        {/* ===================== LEFT: TREE ===================== */}
        <div style={{ background: colors.surface, border: `1px solid ${colors.border}`, borderRadius: 8, padding: 10 }}>
          {filteredTree.length === 0 ? (
            <div style={{ padding: 24 }}>
              <EmptyState title="No WBS nodes yet" description="Add a root node to start building the work breakdown structure." />
            </div>
          ) : (
            filteredTree.map((node) => (
              <TreeNode
                key={node.id}
                node={node}
                depth={0}
                expanded={expanded}
                onToggle={toggleExpand}
                selectedId={selectedId}
                onSelect={setSelectedId}
                boqCounts={boqCounts}
                costByNode={costByNode}
                isFrozen={isFrozen}
                onAddChild={(parentId) => setShowAddForm(parentId)}
                onDelete={handleDelete}
                onMoveUp={(id) => handleReorder(id, 'up')}
                onMoveDown={(id) => handleReorder(id, 'down')}
              />
            ))
          )}
        </div>

        {/* ===================== RIGHT: DETAIL PANEL ===================== */}
        <div style={{ background: colors.surface, border: `1px solid ${colors.border}`, borderRadius: 8, padding: 16, position: 'sticky', top: 16 }}>
          {!selectedNode ? (
            <EmptyState title="Select a node" description="Choose a WBS node from the tree to view its details." />
          ) : (
            <NodeDetailPanel
              node={selectedNode}
              nodes={nodes}
              revisionId={revisionId}
              tenderId={tenderId}
              boqCount={boqCounts[selectedNode.id] || 0}
              cost={costByNode[selectedNode.id]}
              isFrozen={isFrozen}
              onEdit={handleEditNode}
              onMove={handleMove}
              onAssigned={load}
              onError={setError}
              onSuccess={setSuccessMsg}
            />
          )}
        </div>
      </div>
    </div>
  )
}

// -----------------------------------------------------------------------
// Tree building / filtering (pure presentation logic)
// -----------------------------------------------------------------------
function buildTree(flatNodes) {
  const byId = {}
  flatNodes.forEach((n) => { byId[n.id] = { ...n, children: [] } })
  const roots = []
  flatNodes.forEach((n) => {
    if (n.parent_id && byId[n.parent_id]) byId[n.parent_id].children.push(byId[n.id])
    else roots.push(byId[n.id])
  })
  return roots
}
function filterTree(tree, query) {
  function walk(nodes) {
    return nodes
      .map((n) => {
        const children = walk(n.children)
        const matches = n.name.toLowerCase().includes(query)
        if (matches || children.length > 0) return { ...n, children }
        return null
      })
      .filter(Boolean)
  }
  return walk(tree)
}

// -----------------------------------------------------------------------
// TreeNode — visual pattern derived from the real CostCodes.jsx tree row
// -----------------------------------------------------------------------
function TreeNode({ node, depth, expanded, onToggle, selectedId, onSelect, boqCounts, costByNode, isFrozen, onAddChild, onDelete, onMoveUp, onMoveDown }) {
  const [menuOpen, setMenuOpen] = useState(false)
  const isExpanded = expanded.has(node.id)
  const isSelected = selectedId === node.id
  const hasChildren = node.children.length > 0
  const boqCount = boqCounts[node.id] || 0

  return (
    <div>
      <div
        onClick={() => onSelect(node.id)}
        style={{
          display: 'flex', alignItems: 'center', gap: 6, padding: '7px 8px', borderRadius: 6, cursor: 'pointer',
          marginLeft: depth * 20, marginBottom: 2,
          background: isSelected ? '#F3F4F6' : 'transparent',
          border: isSelected ? '1px solid rgba(26,122,74,0.3)' : '1px solid transparent',
        }}
        onMouseEnter={(e) => { if (!isSelected) e.currentTarget.style.background = '#FAFBFC' }}
        onMouseLeave={(e) => { if (!isSelected) e.currentTarget.style.background = 'transparent' }}
      >
        <button
          onClick={(e) => { e.stopPropagation(); if (hasChildren) onToggle(node.id) }}
          style={{ background: 'none', border: 'none', cursor: hasChildren ? 'pointer' : 'default', color: colors.textMuted, width: 16, fontWeight: 700, fontSize: 11 }}
        >
          {hasChildren ? (isExpanded ? '▼' : '▶') : '·'}
        </button>

        <span style={{ fontSize: 13, fontWeight: isSelected ? 700 : 500, color: colors.textPrimary, flex: 1 }}>{node.name}</span>

        {node.is_costing_package && (
          <span style={{ fontSize: 9, fontWeight: 700, padding: '1px 6px', borderRadius: 10, background: '#F0FDF4', color: colors.accentGreen, border: '1px solid #86EFAC' }}>
            Package
          </span>
        )}
        {boqCount > 0 && (
          <span style={{ fontSize: 10, color: colors.textMuted }}>{boqCount} item{boqCount !== 1 ? 's' : ''}</span>
        )}

        {!isFrozen && (
          <div style={{ display: 'flex', gap: 2, position: 'relative' }}>
            <button onClick={(e) => { e.stopPropagation(); onAddChild(node.id) }} title="Add Child" style={iconBtnStyle}>+</button>
            <button onClick={(e) => { e.stopPropagation(); setMenuOpen((v) => !v) }} title="More actions" style={iconBtnStyle}>⋮</button>
            {menuOpen && (
              <div style={{ position: 'absolute', top: 20, right: 0, zIndex: 10, background: '#fff', border: `1px solid ${colors.border}`, borderRadius: 6, boxShadow: '0 4px 12px rgba(0,0,0,0.1)', minWidth: 130 }}>
                <MenuItem onClick={() => { onMoveUp(node.id); setMenuOpen(false) }}>▲ Move Up</MenuItem>
                <MenuItem onClick={() => { onMoveDown(node.id); setMenuOpen(false) }}>▼ Move Down</MenuItem>
                <MenuItem danger onClick={() => { onDelete(node.id); setMenuOpen(false) }}>✕ Delete</MenuItem>
              </div>
            )}
          </div>
        )}
      </div>

      {hasChildren && isExpanded && node.children.map((child) => (
        <TreeNode
          key={child.id} node={child} depth={depth + 1} expanded={expanded} onToggle={onToggle}
          selectedId={selectedId} onSelect={onSelect} boqCounts={boqCounts} costByNode={costByNode}
          isFrozen={isFrozen} onAddChild={onAddChild} onDelete={onDelete} onMoveUp={onMoveUp} onMoveDown={onMoveDown}
        />
      ))}
    </div>
  )
}

const iconBtnStyle = { background: 'none', border: `1px solid ${colors.border}`, borderRadius: 4, cursor: 'pointer', color: colors.textSecondary, width: 20, height: 20, fontSize: 12, lineHeight: 1 }

function MenuItem({ children, onClick, danger }) {
  return (
    <button
      onClick={onClick}
      style={{ display: 'block', width: '100%', textAlign: 'left', padding: '7px 10px', background: 'none', border: 'none', cursor: 'pointer', fontSize: 12, color: danger ? colors.danger : colors.textPrimary }}
    >
      {children}
    </button>
  )
}

// -----------------------------------------------------------------------
// Node detail panel
// -----------------------------------------------------------------------
function NodeDetailPanel({ node, nodes, revisionId, tenderId, boqCount, cost, isFrozen, onEdit, onMove, onAssigned, onError, onSuccess }) {
  const [editing, setEditing] = useState(false)
  const [assignedItems, setAssignedItems] = useState(null)
  const [showAssign, setShowAssign] = useState(false)
  const [showAllBoq, setShowAllBoq] = useState(false)

  const parentNode = nodes.find((n) => n.id === node.parent_id)
  const level = computeLevel(node, nodes)

  const loadAssigned = useCallback(async () => {
    const { data, error } = await supabase
      .from('boq_item_wbs_map')
      .select('boq_item_id, boq_item!fk_map_boq_same_revision(id, item_no, description, unit, resolved_quantity)')
      .eq('wbs_snapshot_node_id', node.id)
    if (error) return onError(error.message)
    setAssignedItems(data.map((d) => d.boq_item))
  }, [node.id, onError])

  useEffect(() => { setShowAllBoq(false); loadAssigned() }, [node.id, loadAssigned])

  async function handleRemoveAssignment(boqItemId) {
    const { error } = await supabase.from('boq_item_wbs_map').delete().eq('boq_item_id', boqItemId).eq('wbs_snapshot_node_id', node.id)
    if (error) onError(error.message)
    else { onSuccess('Assignment removed.'); await loadAssigned(); onAssigned() }
  }

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}>
        <div>
          <div style={{ fontSize: 16, fontWeight: 700, color: colors.textPrimary }}>{node.name}</div>
          {node.is_costing_package && (
            <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 10, background: '#F0FDF4', color: colors.accentGreen, border: '1px solid #86EFAC', marginTop: 4, display: 'inline-block' }}>
              Costing Package
            </span>
          )}
        </div>
        {!isFrozen && <Button size="sm" variant="secondary" onClick={() => setEditing(true)}>Edit</Button>}
      </div>

      {editing ? (
        <NodeForm
          title="Edit Node"
          initialName={node.name}
          initialIsPackage={node.is_costing_package}
          onSubmit={(name, isPackage) => { onEdit(node.id, name, isPackage); setEditing(false) }}
          onCancel={() => setEditing(false)}
        />
      ) : (
        <>
          <DetailRow label="Parent" value={parentNode?.name || '— (root)'} />
          <DetailRow label="Level" value={level} />

          <div style={{ marginTop: 16, marginBottom: 8, fontSize: 11, fontWeight: 700, color: colors.textSecondary, textTransform: 'uppercase' }}>Cost</div>
          <div style={{ fontSize: 18, fontWeight: 800, color: cost != null ? colors.primary : colors.textMuted }}>
            {cost != null ? `${formatNumber(cost)} SAR` : 'No cost data available'}
          </div>

          <div style={{ marginTop: 16, marginBottom: 8, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ fontSize: 11, fontWeight: 700, color: colors.textSecondary, textTransform: 'uppercase' }}>
              BOQ Items ({assignedItems === null ? boqCount : assignedItems.length})
            </span>
            {!isFrozen && <Button size="sm" onClick={() => setShowAssign(true)}>+ Assign</Button>}
          </div>

          {assignedItems === null ? (
            <SkeletonRows rows={2} cols={1} />
          ) : (
            <BoqItemsList
              items={assignedItems}
              showAll={showAllBoq}
              onShowAll={() => setShowAllBoq(true)}
              onCollapse={() => setShowAllBoq(false)}
              isFrozen={isFrozen}
              tenderId={tenderId}
              revisionId={revisionId}
              onRemove={handleRemoveAssignment}
            />
          )}

          {showAssign && (
            <AssignBoqModal
              revisionId={revisionId}
              wbsNodeId={node.id}
              wbsNodeName={node.name}
              allNodes={nodes}
              onClose={() => setShowAssign(false)}
              onAssigned={async () => { setShowAssign(false); await loadAssigned(); onAssigned() }}
              onError={onError}
            />
          )}
        </>
      )}
    </div>
  )
}

// -----------------------------------------------------------------------
// BOQ Items list — bounded, non-expanding-page presentation. Shows the
// first 5 items by default; "Show all N items" reveals the rest inside a
// fixed-height scrollable container, never growing the page itself.
// -----------------------------------------------------------------------
const BOQ_LIST_PREVIEW_COUNT = 5

function BoqItemsList({ items, showAll, onShowAll, onCollapse, isFrozen, tenderId, revisionId, onRemove }) {
  if (items.length === 0) {
    return <div style={{ fontSize: 12, color: colors.textMuted }}>No BOQ items assigned to this node.</div>
  }

  const visibleItems = showAll ? items : items.slice(0, BOQ_LIST_PREVIEW_COUNT)
  const hiddenCount = items.length - BOQ_LIST_PREVIEW_COUNT

  const rows = (
    <>
      {visibleItems.map((it) => (
        <div key={it.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 0', borderBottom: `1px solid ${colors.border}`, fontSize: 12 }}>
          <div>
            <Link to={`/estimation/tender/${tenderId}/revision/${revisionId}/rate-build-up?item=${it.id}`} style={{ color: colors.primary, textDecoration: 'none', fontWeight: 600 }}>
              {it.item_no || '—'} — {it.description}
            </Link>
            <div style={{ color: colors.textMuted }}>{it.unit} · {formatNumber(it.resolved_quantity)}</div>
          </div>
          {!isFrozen && (
            <button onClick={() => onRemove(it.id)} style={{ background: 'none', border: 'none', color: colors.danger, cursor: 'pointer', fontSize: 11, flexShrink: 0, marginLeft: 8 }}>Remove</button>
          )}
        </div>
      ))}
    </>
  )

  return (
    <div>
      {showAll ? (
        // Bounded scrollable container — only this list scrolls, never the page.
        <div style={{ maxHeight: 280, overflowY: 'auto', border: `1px solid ${colors.border}`, borderRadius: 6, padding: '0 8px' }}>
          {rows}
        </div>
      ) : (
        rows
      )}

      {items.length > BOQ_LIST_PREVIEW_COUNT && (
        <button
          onClick={showAll ? onCollapse : onShowAll}
          style={{ background: 'none', border: 'none', color: colors.primary, cursor: 'pointer', fontSize: 12, fontWeight: 600, marginTop: 8, padding: 0 }}
        >
          {showAll ? '↑ Collapse' : `Show all ${items.length} items`}
        </button>
      )}
    </div>
  )
}

function computeLevel(node, nodes) {
  let level = 1
  let current = node
  while (current.parent_id) {
    current = nodes.find((n) => n.id === current.parent_id)
    if (!current) break
    level++
  }
  return level
}

function DetailRow({ label, value }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '5px 0', borderBottom: `1px solid ${colors.border}`, fontSize: 12 }}>
      <span style={{ color: colors.textMuted }}>{label}</span>
      <span style={{ color: colors.textPrimary, fontWeight: 600 }}>{value}</span>
    </div>
  )
}

// -----------------------------------------------------------------------
// Add / Edit form
// -----------------------------------------------------------------------
function NodeForm({ title, initialName = '', initialIsPackage = false, onSubmit, onCancel }) {
  const [name, setName] = useState(initialName)
  const [isPackage, setIsPackage] = useState(initialIsPackage)

  return (
    <div style={{ padding: 14, background: '#FAFBFC', border: `1px solid ${colors.border}`, borderRadius: 8, marginBottom: 14 }}>
      <div style={{ fontSize: 12, fontWeight: 700, color: colors.textSecondary, marginBottom: 8, textTransform: 'uppercase' }}>{title}</div>
      <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end', flexWrap: 'wrap' }}>
        <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Node name…" style={{ flex: 1, minWidth: 160, fontSize: 13 }} />
        <label style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 12, color: colors.textSecondary }}>
          <input type="checkbox" checked={isPackage} onChange={(e) => setIsPackage(e.target.checked)} /> Costing Package
        </label>
        <Button size="sm" onClick={() => name.trim() && onSubmit(name.trim(), isPackage)}>Save</Button>
        <Button size="sm" variant="secondary" onClick={onCancel}>Cancel</Button>
      </div>
    </div>
  )
}

// -----------------------------------------------------------------------
// Assign BOQ Items panel — shows unassigned items (or items assigned
// elsewhere, with a reassignment warning) for this revision.
// -----------------------------------------------------------------------
// =======================================================================
// AssignBoqModal — bounded modal with multi-select, search, filters, and
// an explicit confirmation step before moving items already assigned to
// another WBS node. Uses the existing assign_boq_to_wbs_node RPC for
// every write — this component owns selection/presentation state only;
// the backend remains sole authority on the one-item-one-node rule,
// frozen-revision protection, and RLS.
// =======================================================================
const ASSIGN_FILTERS = [
  { key: 'all', label: 'All' },
  { key: 'unassigned', label: 'Unassigned' },
  { key: 'assigned', label: 'Assigned (any node)' },
  { key: 'this_node', label: 'Assigned to this node' },
]

function AssignBoqModal({ revisionId, wbsNodeId, wbsNodeName, allNodes, onClose, onAssigned, onError }) {
  const [items, setItems] = useState(null)
  const [search, setSearch] = useState('')
  const [filter, setFilter] = useState('unassigned')
  const [selected, setSelected] = useState(new Set())
  const [confirming, setConfirming] = useState(false)
  const [assigning, setAssigning] = useState(false)

  const nodeNameById = useMemo(() => Object.fromEntries(allNodes.map((n) => [n.id, n.name])), [allNodes])

  useEffect(() => {
    supabase
      .from('boq_item_pricing_status')
      .select('boq_item_id, item_no, description, unit, pricing_status')
      .eq('estimate_revision_id', revisionId)
      .then(async ({ data, error }) => {
        if (error) return onError(error.message)
        const { data: mapped } = await supabase.from('boq_item_wbs_map').select('boq_item_id, wbs_snapshot_node_id').eq('estimate_revision_id', revisionId)
        const mappedInfo = Object.fromEntries((mapped || []).map((m) => [m.boq_item_id, m.wbs_snapshot_node_id]))
        setItems(data.map((it) => ({ ...it, currentNodeId: mappedInfo[it.boq_item_id] || null })))
      })
  }, [revisionId, onError])

  const filtered = useMemo(() => {
    if (!items) return []
    const q = search.trim().toLowerCase()
    return items.filter((it) => {
      if (q && !it.description.toLowerCase().includes(q) && !(it.item_no || '').toLowerCase().includes(q) && !it.unit.toLowerCase().includes(q)) return false
      if (filter === 'unassigned') return !it.currentNodeId
      if (filter === 'assigned') return !!it.currentNodeId
      if (filter === 'this_node') return it.currentNodeId === wbsNodeId
      return true
    })
  }, [items, search, filter, wbsNodeId])

  // "Already assigned to this node" items are never selectable — nothing
  // to do for them (avoids a meaningless self-reassignment RPC call).
  const selectableFiltered = filtered.filter((it) => it.currentNodeId !== wbsNodeId)
  const allVisibleSelected = selectableFiltered.length > 0 && selectableFiltered.every((it) => selected.has(it.boq_item_id))

  function toggleOne(boqItemId) {
    setSelected((prev) => {
      const next = new Set(prev)
      if (next.has(boqItemId)) next.delete(boqItemId)
      else next.add(boqItemId)
      return next
    })
  }
  function toggleSelectAllVisible() {
    setSelected((prev) => {
      const next = new Set(prev)
      if (allVisibleSelected) {
        selectableFiltered.forEach((it) => next.delete(it.boq_item_id))
      } else {
        selectableFiltered.forEach((it) => next.add(it.boq_item_id))
      }
      return next
    })
  }
  function clearSelection() { setSelected(new Set()) }

  const selectedItems = (items || []).filter((it) => selected.has(it.boq_item_id))
  const movingItems = selectedItems.filter((it) => it.currentNodeId && it.currentNodeId !== wbsNodeId)

  async function performAssign() {
    setAssigning(true)
    for (const it of selectedItems) {
      const { error } = await supabase.rpc('assign_boq_to_wbs_node', { p_boq_item_id: it.boq_item_id, p_wbs_node_id: wbsNodeId })
      if (error) { onError(error.message); setAssigning(false); return }
    }
    setAssigning(false)
    onAssigned()
  }

  function handleAssignClick() {
    if (selected.size === 0) return
    if (movingItems.length > 0) setConfirming(true)
    else performAssign()
  }

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(15,16,23,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100 }} onClick={onClose}>
      <div
        onClick={(e) => e.stopPropagation()}
        style={{ background: '#fff', borderRadius: 10, width: 'min(680px, 92vw)', maxHeight: '85vh', display: 'flex', flexDirection: 'column', boxShadow: '0 12px 40px rgba(0,0,0,0.2)' }}
      >
        {confirming ? (
          <MoveConfirmation
            movingItems={movingItems}
            nodeNameById={nodeNameById}
            targetNodeName={wbsNodeName}
            assigning={assigning}
            onCancel={() => setConfirming(false)}
            onConfirm={performAssign}
          />
        ) : (
          <>
            {/* Sticky header */}
            <div style={{ padding: '14px 18px', borderBottom: `1px solid ${colors.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0 }}>
              <span style={{ fontSize: 13, fontWeight: 700, color: colors.textPrimary, textTransform: 'uppercase', letterSpacing: 0.3 }}>Assign BOQ Items</span>
              <button onClick={onClose} style={{ background: 'none', border: 'none', color: colors.textMuted, cursor: 'pointer', fontSize: 16 }}>✕</button>
            </div>

            {/* Search + filters */}
            <div style={{ padding: '12px 18px', borderBottom: `1px solid ${colors.border}`, flexShrink: 0 }}>
              <Input placeholder="Search item no, description, unit…" value={search} onChange={(e) => setSearch(e.target.value)} style={{ width: '100%', fontSize: 13, marginBottom: 10 }} />
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                {ASSIGN_FILTERS.map((f) => (
                  <button
                    key={f.key}
                    onClick={() => setFilter(f.key)}
                    style={{
                      padding: '4px 10px', borderRadius: 12, fontSize: 11, fontWeight: 600, cursor: 'pointer',
                      border: `1px solid ${filter === f.key ? colors.primary : colors.border}`,
                      background: filter === f.key ? '#EEF2FF' : '#fff', color: filter === f.key ? colors.primary : colors.textSecondary,
                    }}
                  >
                    {f.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Select all visible / clear */}
            <div style={{ padding: '8px 18px', borderBottom: `1px solid ${colors.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0 }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: colors.textSecondary, cursor: selectableFiltered.length > 0 ? 'pointer' : 'default' }}>
                <input type="checkbox" checked={allVisibleSelected} disabled={selectableFiltered.length === 0} onChange={toggleSelectAllVisible} />
                Select all visible ({selectableFiltered.length})
              </label>
              {selected.size > 0 && (
                <button onClick={clearSelection} style={{ background: 'none', border: 'none', color: colors.textMuted, cursor: 'pointer', fontSize: 11 }}>Clear selection</button>
              )}
            </div>

            {/* Scrollable item list — ONLY this scrolls */}
            <div style={{ overflowY: 'auto', flex: 1, padding: '4px 18px' }}>
              {items === null ? (
                <SkeletonRows rows={4} cols={1} />
              ) : filtered.length === 0 ? (
                <div style={{ padding: '20px 0', fontSize: 12, color: colors.textMuted, textAlign: 'center' }}>No matching BOQ items.</div>
              ) : (
                filtered.map((it) => {
                  const isThisNode = it.currentNodeId === wbsNodeId
                  const isElsewhere = it.currentNodeId && !isThisNode
                  return (
                    <label
                      key={it.boq_item_id}
                      style={{
                        display: 'flex', alignItems: 'flex-start', gap: 10, padding: '9px 6px', borderBottom: `1px solid ${colors.border}`,
                        cursor: isThisNode ? 'default' : 'pointer', opacity: isThisNode ? 0.6 : 1,
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={selected.has(it.boq_item_id)}
                        disabled={isThisNode}
                        onChange={() => toggleOne(it.boq_item_id)}
                        style={{ marginTop: 3, width: 15, height: 15, cursor: isThisNode ? 'default' : 'pointer' }}
                      />
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: 13, color: colors.textPrimary }}>
                          <strong>{it.item_no || '—'}</strong> — {it.description}
                          <span style={{ color: colors.textMuted, fontWeight: 400 }}> · {it.unit}</span>
                        </div>
                        {isThisNode && <div style={{ fontSize: 10, color: colors.accentGreen, marginTop: 2 }}>Already here</div>}
                        {isElsewhere && (
                          <div style={{ fontSize: 10, color: colors.warningText, marginTop: 2 }}>
                            Assigned to: {nodeNameById[it.currentNodeId] || 'another node'}
                          </div>
                        )}
                      </div>
                    </label>
                  )
                })
              )}
            </div>

            {/* Sticky footer */}
            <div style={{ padding: '12px 18px', borderTop: `1px solid ${colors.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0 }}>
              <span style={{ fontSize: 12, color: colors.textSecondary }}>{selected.size} item{selected.size !== 1 ? 's' : ''} selected</span>
              <div style={{ display: 'flex', gap: 8 }}>
                <Button variant="secondary" onClick={onClose}>Cancel</Button>
                <Button onClick={handleAssignClick} disabled={selected.size === 0}>Assign {selected.size > 0 ? `${selected.size} Item${selected.size !== 1 ? 's' : ''}` : ''}</Button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  )
}

function MoveConfirmation({ movingItems, nodeNameById, targetNodeName, assigning, onCancel, onConfirm }) {
  return (
    <>
      <div style={{ padding: '14px 18px', borderBottom: `1px solid ${colors.border}` }}>
        <span style={{ fontSize: 13, fontWeight: 700, color: colors.warningText, textTransform: 'uppercase', letterSpacing: 0.3 }}>⚠ Move BOQ Items</span>
      </div>
      <div style={{ padding: '14px 18px', overflowY: 'auto', flex: 1 }}>
        <p style={{ fontSize: 12, color: colors.textSecondary, marginTop: 0 }}>
          {movingItems.length} item{movingItems.length !== 1 ? 's are' : ' is'} currently assigned to another WBS node. Assigning here will move {movingItems.length !== 1 ? 'them' : 'it'}.
        </p>
        {movingItems.map((it) => (
          <div key={it.boq_item_id} style={{ padding: '8px 0', borderBottom: `1px solid ${colors.border}`, fontSize: 12 }}>
            <div style={{ fontWeight: 600, color: colors.textPrimary }}>{it.item_no || '—'} — {it.description}</div>
            <div style={{ color: colors.textMuted, marginTop: 2 }}>
              Currently: <strong>{nodeNameById[it.currentNodeId] || '—'}</strong> → New: <strong style={{ color: colors.primary }}>{targetNodeName}</strong>
            </div>
          </div>
        ))}
      </div>
      <div style={{ padding: '12px 18px', borderTop: `1px solid ${colors.border}`, display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
        <Button variant="secondary" onClick={onCancel} disabled={assigning}>Cancel</Button>
        <Button onClick={onConfirm} disabled={assigning}>{assigning ? 'Assigning…' : 'Confirm Move & Assign'}</Button>
      </div>
    </>
  )
}
